import { parseClaimLimitFromReason } from "@/app/drop/store/get-eligibility-query"
import { useIsMobileEmailUser } from "@/app/hooks/use-is-mobile-email-user"
import { useCollectionPageHref } from "@/app/navigation/collection"
import { CollectionPageType } from "@/app/navigation/collection/collection-page-type"
import { DropMintMiniature } from "@/app/nft/miniautre/nft-miniature"
import { PriceInCrypto } from "@/app/price"
import { getDropCurrentPhase } from "@/app/utils/get-drop-current-phase"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { MinimalDrop } from "@anaconda/core/drop/types"
import { toCollectionId } from "@anaconda/core/nft-collection/utils"
import { Trans } from "@lingui/react/macro"
import { parseUnionAddress } from "@rarible_int/anaconda_sdk/ethereum"
import Link from "next/link"
import { AppToast } from "./app-toast"
import { ToastItemName } from "./toast-item-name"
import { useToastError } from "./toast-util"

export const MintToast = {
  Error: function MintToastError({
    drop,
    id,
    error,
    quantity = 1,
  }: {
    drop: MinimalDrop
    id: string | number
    error: unknown
    quantity?: number
  }) {
    const { message, type } = useToastError(error, drop.blockchain)

    const customMessage = getCustomDropClaimExceedLimitMessage(error, quantity)

    const title = getMintText({ drop, quantity })
    const main = <MintLink drop={drop} quantity={quantity} />

    if (type === "destructive") {
      return (
        <AppToast.Error
          id={id}
          title={title}
          image={<DropMintMiniature drop={drop} />}
          main={main}
          second={
            <div className="font-semibold text-destructive">
              {customMessage || message}
            </div>
          }
        />
      )
    } else if (type === "surface") {
      return (
        <AppToast.Warning
          id={id}
          image={<DropMintMiniature drop={drop} />}
          title={title}
          main={main}
          second={
            <div className="font-semibold text-muted-foreground">
              {customMessage || message}
            </div>
          }
        />
      )
    }
  },
  Pending: function MintToastPending({
    drop,
    id,
    quantity = 1,
  }: {
    drop: MinimalDrop
    id: string | number
    quantity?: number
  }) {
    const isMobileEmailUser = useIsMobileEmailUser()

    if (isMobileEmailUser) {
      return null
    }

    const currentPhase = getDropCurrentPhase(drop.phases)
    const totalPrice = currentPhase && {
      amount: (Number(currentPhase.price.amount) * quantity).toString(),
      currency: currentPhase.price.currency,
    }

    return (
      <AppToast.Pending
        id={id}
        image={<DropMintMiniature drop={drop} />}
        title={getMintText({ drop, quantity })}
        main={
          <Trans>
            Minting {quantity}{" "}
            <Link href={""} className="text-brand">
              <ToastItemName name={drop.title} />
            </Link>
            {totalPrice && (
              <>
                {" "}
                for <PriceInCrypto {...totalPrice} />
              </>
            )}
          </Trans>
        }
        second={
          <div className="font-semibold text-muted-foreground">
            <Trans>Pending...</Trans>
          </div>
        }
      />
    )
  },
  Success: function MintToastSuccess({
    drop,
    id,
    quantity = 1,
    title,
  }: {
    drop: MinimalDrop
    id: string | number
    quantity?: number
    title?: string
  }) {
    const href = useCollectionPageHref(drop.id, CollectionPageType.MY_ITEMS)
    const defaultTitle = `Congrats! ${quantity} ${drop.title} ${quantity === 1 ? "is" : "are"} yours!`

    return (
      <AppToast.Success
        id={id}
        image={<DropMintMiniature drop={drop} />}
        title={title ?? defaultTitle}
        main={<Trans>{title ?? defaultTitle}</Trans>}
        second={
          <div className="font-semibold text-brand">
            <Link href={href}>
              <Trans>View your items</Trans>
            </Link>
          </div>
        }
      />
    )
  },
}

function getMintText({
  drop,
  quantity = 1,
}: { drop: MinimalDrop; quantity?: number }) {
  return `Mint ${quantity} ${drop.title}`
}

function MintLink({
  drop,
  quantity = 1,
}: { drop: MinimalDrop; quantity?: number }) {
  return (
    <Trans>
      Mint {quantity}{" "}
      <Link href={""} className="text-brand">
        {drop.title}
      </Link>
    </Trans>
  )
}

function getCustomDropClaimExceedLimitMessage(
  error: unknown,
  quantity: number,
): React.ReactNode | null {
  if (!error || typeof error !== "object" || !("message" in error)) {
    return null
  }

  const errorMessage = String(error.message)
  const dropClaimExceedLimit = errorMessage.includes("DropClaimExceedLimit")

  if (!dropClaimExceedLimit) {
    return null
  }

  const claimLimitInfo = parseClaimLimitFromReason(errorMessage, quantity)

  if (claimLimitInfo) {
    const { limitPerWallet, alreadyMinted, remainingLimit } = claimLimitInfo

    if (remainingLimit <= 0) {
      return (
        <Trans>
          Wallet limit exceeded. <br />
          You've already minted {alreadyMinted} items from your limit of{" "}
          {limitPerWallet}.
        </Trans>
      )
    }

    return (
      <Trans>
        Wallet limit exceeded. You can mint {remainingLimit} more item
        {remainingLimit !== 1 ? "s" : ""} from your limit of {limitPerWallet}.
      </Trans>
    )
  }

  return null
}
